import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const ETAPAS = {
    UPLOAD:      1,
    MAPEAMENTO:  2,
    RESULTADO:   3,
};

const CORES = {
    primary:   '#3b82f6',
    success:   '#10b981',
    warning:   '#f59e0b',
    danger:    '#ef4444',
    gray100:   '#f3f4f6',
    gray200:   '#e5e7eb',
    gray400:   '#9ca3af',
    gray600:   '#6b7280',
    gray700:   '#374151',
    gray900:   '#111827',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: Stepper
// ─────────────────────────────────────────────────────────────────────────────

const Stepper = ({ etapaAtual }) => {
    const steps = [
        { num: 1, label: 'Upload do Arquivo' },
        { num: 2, label: 'Mapear Colunas'    },
        { num: 3, label: 'Resultado'          },
    ];

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 0 8px' }}>
            {steps.map((step, idx) => (
                <React.Fragment key={step.num}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '32px', height: '32px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700',
                            backgroundColor: etapaAtual > step.num
                                ? CORES.success
                                : etapaAtual === step.num
                                    ? CORES.primary
                                    : CORES.gray200,
                            color: etapaAtual >= step.num ? '#fff' : CORES.gray600,
                            transition: 'all 0.3s',
                        }}>
                            {etapaAtual > step.num
                                ? <FontAwesomeIcon icon="check" style={{ fontSize: `calc(12px * var(--font-scale))` }} />
                                : step.num
                            }
                        </div>
                        <span style={{
                            fontSize: `calc(11px * var(--font-scale))`, fontWeight: etapaAtual === step.num ? '600' : '400',
                            color: etapaAtual === step.num ? CORES.primary : CORES.gray600,
                            whiteSpace: 'nowrap',
                        }}>
                            {step.label}
                        </span>
                    </div>

                    {idx < steps.length - 1 && (
                        <div style={{
                            flex: 1, height: '2px', margin: '0 8px', marginBottom: '22px',
                            backgroundColor: etapaAtual > step.num ? CORES.success : CORES.gray200,
                            transition: 'background-color 0.3s',
                        }} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: Etapa 1 — Upload
// ─────────────────────────────────────────────────────────────────────────────

const EtapaUpload = ({ onPreviewSuccess, onError }) => {
    const [isDragging,   setIsDragging]   = useState(false);
    const [arquivo,      setArquivo]      = useState(null);
    const [isCarregando, setIsCarregando] = useState(false);
    const inputRef = useRef(null);

    const processarArquivo = useCallback(async (file) => {
        if (!file) return;

        const ext = file.name.split('.').pop().toLowerCase();
        if (!['json', 'csv', 'txt'].includes(ext)) {
            onError('Formato inválido. Envie um arquivo .json, .csv ou .txt');
            return;
        }

        setArquivo(file);
        setIsCarregando(true);

        try {
            const formData = new FormData();
            formData.append('arquivo', file);

            const response = await axios.post('/api/leads/import/preview', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            onPreviewSuccess(response.data);
        } catch (err) {
            onError(err.response?.data?.message || 'Erro ao processar o arquivo.');
            setArquivo(null);
        } finally {
            setIsCarregando(false);
        }
    }, [onPreviewSuccess, onError]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) processarArquivo(file);
    }, [processarArquivo]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) processarArquivo(file);
    };

    return (
        <div style={{ padding: '8px 0' }}>
            <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => !isCarregando && inputRef.current?.click()}
                style={{
                    border: `2px dashed ${isDragging ? CORES.primary : CORES.gray200}`,
                    borderRadius: '12px',
                    padding: '48px 24px',
                    textAlign: 'center',
                    cursor: isCarregando ? 'not-allowed' : 'pointer',
                    backgroundColor: isDragging ? '#eff6ff' : CORES.gray100,
                    transition: 'all 0.2s',
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".json,.csv,.txt"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />

                {isCarregando ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            border: `3px solid ${CORES.gray200}`,
                            borderTopColor: CORES.primary,
                            animation: 'spin 0.8s linear infinite',
                        }} />
                        <p style={{ margin: 0, fontSize: `calc(14px * var(--font-scale))`, color: CORES.gray600 }}>
                            Analisando arquivo...
                        </p>
                    </div>
                ) : arquivo ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            width: '48px', height: '48px', borderRadius: '12px',
                            backgroundColor: '#dcfce7',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon="file-alt" style={{ fontSize: '22px', color: CORES.success }} />
                        </div>
                        <p style={{ margin: 0, fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600', color: CORES.gray700 }}>
                            {arquivo.name}
                        </p>
                        <p style={{ margin: 0, fontSize: `calc(12px * var(--font-scale))`, color: CORES.gray400 }}>
                            {(arquivo.size / 1024).toFixed(1)} KB
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '56px', height: '56px', borderRadius: '16px',
                            backgroundColor: '#dbeafe',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon="cloud-upload-alt" style={{ fontSize: '26px', color: CORES.primary }} />
                        </div>
                        <div>
                            <p style={{ margin: '0 0 4px', fontSize: `calc(15px * var(--font-scale))`, fontWeight: '600', color: CORES.gray700 }}>
                                Arraste seu arquivo aqui
                            </p>
                            <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, color: CORES.gray400 }}>
                                ou <span style={{ color: CORES.primary, fontWeight: '600' }}>clique para selecionar</span>
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                            {['JSON', 'CSV', 'TXT'].map(fmt => (
                                <span key={fmt} style={{
                                    padding: '2px 10px', borderRadius: '999px',
                                    backgroundColor: '#fff', border: `1px solid ${CORES.gray200}`,
                                    fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: CORES.gray600,
                                }}>
                                    .{fmt}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div style={{
                marginTop: '16px', padding: '12px 16px',
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                display: 'flex', gap: '10px', alignItems: 'flex-start',
            }}>
                <FontAwesomeIcon icon="lightbulb" style={{ color: CORES.warning, fontSize: `calc(14px * var(--font-scale))`, marginTop: '1px', flexShrink: 0 }} />
                <div>
                    <p style={{ margin: '0 0 4px', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#92400e' }}>
                        Formatos aceitos
                    </p>
                    <p style={{ margin: 0, fontSize: `calc(12px * var(--font-scale))`, color: '#78350f', lineHeight: '1.5' }}>
                        <strong>JSON:</strong> array de objetos ou com chave <code>data</code>, <code>leads</code> ou <code>contacts</code><br />
                        <strong>CSV:</strong> primeira linha deve ser o cabeçalho com os nomes das colunas
                    </p>
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: Etapa 2 — Mapeamento
// ─────────────────────────────────────────────────────────────────────────────

const EtapaMapeamento = ({
    previewData,
    pipelines,
    colaboradores,
    mapping,
    setMapping,
    config,
    setConfig,
}) => {
    const { colunas, amostra, total_registros, campos_sistema } = previewData;

    const getStatusMapping = (campo) => {
        if (campo === '_ignorar') return 'ignorar';
        if (!campo) return 'nao_mapeado';
        return 'mapeado';
    };

    const estagiosDaPipeline = pipelines.find(p => p.id === parseInt(config.pipeline_id))?.estagios || [];
    const totalMapeadas = Object.values(mapping).filter(v => v && v !== '_ignorar').length;
    const temNome = Object.values(mapping).includes('nome');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Resumo do arquivo */}
            <div style={{
                display: 'flex', gap: '12px',
                padding: '12px 16px',
                backgroundColor: '#eff6ff',
                borderRadius: '10px',
                border: '1px solid #bfdbfe',
                alignItems: 'center',
            }}>
                <FontAwesomeIcon icon="file-alt" style={{ color: CORES.primary, fontSize: '18px' }} />
                <div>
                    <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: CORES.gray700 }}>
                        Arquivo carregado com sucesso
                    </p>
                    <p style={{ margin: 0, fontSize: `calc(12px * var(--font-scale))`, color: CORES.gray600 }}>
                        <strong>{total_registros}</strong> registros encontrados •{' '}
                        <strong>{colunas.length}</strong> colunas detectadas •{' '}
                        <strong>{totalMapeadas}</strong> mapeadas
                    </p>
                </div>
                {!temNome && (
                    <div style={{
                        marginLeft: 'auto', padding: '4px 10px',
                        backgroundColor: '#fef3c7', borderRadius: '6px',
                        border: '1px solid #fde68a',
                        fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: '#92400e',
                        display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                        <FontAwesomeIcon icon="exclamation-triangle" />
                        Mapeie o campo "Nome"
                    </div>
                )}
            </div>

            {/* Configurações da importação */}
            <div style={{
                padding: '16px',
                border: `1px solid ${CORES.gray200}`,
                borderRadius: '10px',
                backgroundColor: '#fff',
            }}>
                <p style={{ margin: '0 0 12px', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: CORES.gray700 }}>
                    <FontAwesomeIcon icon="sliders-h" style={{ marginRight: '6px', color: CORES.primary }} />
                    Configurações da Importação
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>

                    {/* Pipeline */}
                    <div>
                        <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: CORES.gray600, display: 'block', marginBottom: '4px' }}>
                            Pipeline *
                        </label>
                        <select
                            value={config.pipeline_id}
                            onChange={(e) => {
                                const pid = e.target.value;
                                const pipeline = pipelines.find(p => p.id === parseInt(pid));
                                const primeiroEstagio = pipeline?.estagios?.[0];
                                setConfig(prev => ({
                                    ...prev,
                                    pipeline_id: pid,
                                    estagio_id: primeiroEstagio?.id || '',
                                }));
                            }}
                            style={selectStyle}
                        >
                            <option value="">Selecione...</option>
                            {pipelines.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.is_default ? '★ ' : ''}{p.nome}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Estágio */}
                    <div>
                        <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: CORES.gray600, display: 'block', marginBottom: '4px' }}>
                            Estágio inicial *
                        </label>
                        <select
                            value={config.estagio_id}
                            onChange={(e) => setConfig(prev => ({ ...prev, estagio_id: e.target.value }))}
                            disabled={!config.pipeline_id}
                            style={{ ...selectStyle, opacity: !config.pipeline_id ? 0.5 : 1 }}
                        >
                            <option value="">Selecione...</option>
                            {estagiosDaPipeline.map(e => (
                                <option key={e.id} value={e.id}>{e.nome}</option>
                            ))}
                        </select>
                    </div>

                    {/* Responsável */}
                    <div>
                        <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: CORES.gray600, display: 'block', marginBottom: '4px' }}>
                            Responsável
                        </label>
                        <select
                            value={config.responsavel_id}
                            onChange={(e) => setConfig(prev => ({ ...prev, responsavel_id: e.target.value }))}
                            style={selectStyle}
                        >
                            <option value="">Sem responsável</option>
                            {colaboradores.map(c => (
                                <option key={c.id} value={c.id}>{c.nome}</option>
                            ))}
                        </select>
                    </div>

                    {/* Duplicatas */}
                    <div>
                        <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: CORES.gray600, display: 'block', marginBottom: '4px' }}>
                            Duplicatas (por telefone/email)
                        </label>
                        <select
                            value={config.duplicatas}
                            onChange={(e) => setConfig(prev => ({ ...prev, duplicatas: e.target.value }))}
                            style={selectStyle}
                        >
                            <option value="atualizar">Atualizar existente</option>
                            <option value="ignorar">Ignorar duplicata</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabela de mapeamento */}
            <div style={{
                border: `1px solid ${CORES.gray200}`,
                borderRadius: '10px',
                overflow: 'hidden',
                backgroundColor: '#fff',
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 40px 1fr 120px',
                    padding: '10px 16px',
                    backgroundColor: CORES.gray100,
                    borderBottom: `1px solid ${CORES.gray200}`,
                }}>
                    <span style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: CORES.gray600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Coluna do Arquivo
                    </span>
                    <span />
                    <span style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: CORES.gray600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Campo do Sistema
                    </span>
                    <span style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: CORES.gray600, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                        Status
                    </span>
                </div>

                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    {colunas.map((coluna, idx) => {
                        const valorAtual = mapping[coluna] || '_ignorar';
                        const status = getStatusMapping(valorAtual);
                        const amostraValor = amostra[0]?.[coluna];

                        return (
                            <div
                                key={coluna}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 40px 1fr 120px',
                                    padding: '10px 16px',
                                    borderBottom: idx < colunas.length - 1 ? `1px solid ${CORES.gray100}` : 'none',
                                    backgroundColor: status === 'mapeado' ? '#f0fdf4' : '#fff',
                                    transition: 'background-color 0.15s',
                                    alignItems: 'center',
                                }}
                            >
                                <div>
                                    <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: CORES.gray700 }}>
                                        {coluna}
                                    </p>
                                    {amostraValor && (
                                        <p style={{
                                            margin: '2px 0 0', fontSize: `calc(11px * var(--font-scale))`, color: CORES.gray400,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            maxWidth: '200px',
                                        }}>
                                            ex: {String(amostraValor)}
                                        </p>
                                    )}
                                </div>

                                <div style={{ textAlign: 'center' }}>
                                    <FontAwesomeIcon
                                        icon="arrow-right"
                                        style={{
                                            fontSize: `calc(12px * var(--font-scale))`,
                                            color: status === 'mapeado' ? CORES.success : CORES.gray200,
                                        }}
                                    />
                                </div>

                                <select
                                    value={valorAtual}
                                    onChange={(e) => setMapping(prev => ({ ...prev, [coluna]: e.target.value }))}
                                    style={{
                                        ...selectStyle,
                                        borderColor: status === 'mapeado' ? '#86efac' : CORES.gray200,
                                        backgroundColor: status === 'mapeado' ? '#f0fdf4' : '#fff',
                                        fontWeight: status === 'mapeado' ? '600' : '400',
                                        color: status === 'mapeado' ? '#166534' : CORES.gray600,
                                    }}
                                >
                                    {Object.entries(campos_sistema).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>

                                <div style={{ textAlign: 'center' }}>
                                    {status === 'mapeado' ? (
                                        <span style={{
                                            padding: '2px 10px', borderRadius: '999px',
                                            backgroundColor: '#dcfce7', color: '#166534',
                                            fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600',
                                        }}>
                                            ✓ Mapeado
                                        </span>
                                    ) : (
                                        <span style={{
                                            padding: '2px 10px', borderRadius: '999px',
                                            backgroundColor: CORES.gray100, color: CORES.gray400,
                                            fontSize: `calc(11px * var(--font-scale))`, fontWeight: '500',
                                        }}>
                                            Ignorar
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Preview dos dados */}
            {amostra.length > 0 && (
                <details style={{ cursor: 'pointer' }}>
                    <summary style={{
                        fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: CORES.gray600,
                        padding: '8px 0', userSelect: 'none',
                        display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                        <FontAwesomeIcon icon="eye" />
                        Ver prévia dos dados ({amostra.length} primeiras linhas)
                    </summary>
                    <div style={{
                        marginTop: '8px', overflowX: 'auto',
                        border: `1px solid ${CORES.gray200}`,
                        borderRadius: '8px',
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: `calc(12px * var(--font-scale))` }}>
                            <thead>
                                <tr style={{ backgroundColor: CORES.gray100 }}>
                                    {colunas.map(col => (
                                        <th key={col} style={{
                                            padding: '8px 12px', textAlign: 'left',
                                            fontWeight: '600', color: CORES.gray600,
                                            borderBottom: `1px solid ${CORES.gray200}`,
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {amostra.map((linha, i) => (
                                    <tr key={i} style={{ borderBottom: `1px solid ${CORES.gray100}` }}>
                                        {colunas.map(col => (
                                            <td key={col} style={{
                                                padding: '8px 12px', color: CORES.gray700,
                                                maxWidth: '180px', overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {linha[col] ?? '—'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: Tela de aguardo com barra de progresso (polling)
// ─────────────────────────────────────────────────────────────────────────────

const EtapaAguardando = ({ jobId, onConcluido, onErro, totalInicial }) => {
    const [progresso,   setProgresso]   = useState({ percentual: 0, processados: 0, total: totalInicial || 0 });
    const [statusTexto, setStatusTexto] = useState('Aguardando início...');
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!jobId) return;

        const consultar = async () => {
            try {
                const { data } = await axios.get(`/api/leads/import/status/${jobId}`);

                setProgresso({
                    percentual:  data.percentual  || 0,
                    processados: data.processados || 0,
                    total:       data.total       || 0,
                });

                if (data.status === 'pending') {
                    setStatusTexto('Aguardando na fila...');
                } else if (data.status === 'processing') {
                    setStatusTexto(`Processando leads... (${data.processados} de ${data.total})`);
                } else if (data.status === 'done') {
                    clearInterval(intervalRef.current);
                    setProgresso(p => ({ ...p, percentual: 100 }));
                    setStatusTexto('Concluído!');
                    onConcluido?.(data.resultado);
                } else if (data.status === 'failed') {
                    clearInterval(intervalRef.current);
                    onErro?.(data.erro || 'Erro desconhecido durante a importação.');
                }
            } catch {
                clearInterval(intervalRef.current);
                onErro?.('Não foi possível verificar o status da importação.');
            }
        };

        consultar();
        intervalRef.current = setInterval(consultar, 2000);

        return () => clearInterval(intervalRef.current);
    }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: '24px',
            padding: '40px 20px',
        }}>
            {/* Ícone */}
            <div style={{
                width: '72px', height: '72px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59,130,246,0.35)',
            }}>
                <FontAwesomeIcon icon="file-import" style={{ fontSize: '30px', color: '#fff' }} />
            </div>

            {/* Textos */}
            <div style={{ textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: '700', color: CORES.gray900 }}>
                    Importação em andamento
                </h3>
                <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, color: CORES.gray600 }}>
                    {statusTexto}
                </p>
            </div>

            {/* Barra de progresso */}
            <div style={{ width: '100%', maxWidth: '400px' }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontSize: `calc(12px * var(--font-scale))`,
                    color: CORES.gray600, fontWeight: '600',
                }}>
                    <span>{progresso.processados} de {progresso.total} leads</span>
                    <span>{progresso.percentual}%</span>
                </div>
                <div style={{
                    height: '10px', borderRadius: '999px',
                    backgroundColor: CORES.gray200, overflow: 'hidden',
                }}>
                    <div style={{
                        height: '100%', borderRadius: '999px',
                        background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                        width: `${progresso.percentual}%`,
                        transition: 'width 0.5s ease',
                    }} />
                </div>
            </div>

            {/* Aviso */}
            <div style={{
                padding: '10px 16px', borderRadius: '8px',
                backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                display: 'flex', gap: '8px', alignItems: 'center',
                fontSize: `calc(12px * var(--font-scale))`, color: '#92400e',
            }}>
                <FontAwesomeIcon icon="info-circle" style={{ flexShrink: 0 }} />
                Você pode fechar este modal. A importação continuará em segundo plano.
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: Etapa 3 — Resultado
// ─────────────────────────────────────────────────────────────────────────────

const EtapaResultado = ({ resultado, jobId, onFechar, onNovaImportacao, onConcluido, onErro }) => {

    // Enquanto não há resultado, exibe a tela de polling
    if (!resultado) {
        return (
            <EtapaAguardando
                jobId={jobId}
                onConcluido={onConcluido}
                onErro={onErro}
            />
        );
    }

    const { total, importados, atualizados, ignorados, erros } = resultado;
    const sucesso = importados + atualizados;

    const cards = [
        { label: 'Total',       valor: total,       cor: CORES.primary,  icon: 'list',         bg: '#eff6ff' },
        { label: 'Importados',  valor: importados,  cor: CORES.success,  icon: 'user-plus',    bg: '#f0fdf4' },
        { label: 'Atualizados', valor: atualizados, cor: '#8b5cf6',      icon: 'sync-alt',     bg: '#f5f3ff' },
        { label: 'Ignorados',   valor: ignorados,   cor: CORES.warning,  icon: 'minus-circle', bg: '#fffbeb' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Banner */}
            <div style={{
                padding: '20px', borderRadius: '12px', textAlign: 'center',
                backgroundColor: sucesso > 0 ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${sucesso > 0 ? '#86efac' : '#fecaca'}`,
            }}>
                <div style={{
                    width: '56px', height: '56px', borderRadius: '50%',
                    backgroundColor: sucesso > 0 ? '#dcfce7' : '#fee2e2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 12px',
                }}>
                    <FontAwesomeIcon
                        icon={sucesso > 0 ? 'check-circle' : 'exclamation-circle'}
                        style={{ fontSize: '28px', color: sucesso > 0 ? CORES.success : CORES.danger }}
                    />
                </div>
                <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '700', color: CORES.gray900 }}>
                    {sucesso > 0 ? 'Importação concluída!' : 'Nenhum lead foi importado'}
                </h3>
                <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, color: CORES.gray600 }}>
                    {sucesso > 0
                        ? `${sucesso} lead(s) processado(s) com sucesso`
                        : 'Verifique os erros abaixo e tente novamente'}
                </p>
            </div>

            {/* Cards de estatísticas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                {cards.map(card => (
                    <div key={card.label} style={{
                        padding: '16px', borderRadius: '10px', textAlign: 'center',
                        backgroundColor: card.bg, border: `1px solid ${card.cor}20`,
                    }}>
                        <FontAwesomeIcon icon={card.icon} style={{ fontSize: '20px', color: card.cor, marginBottom: '8px' }} />
                        <p style={{ margin: '0 0 2px', fontSize: '24px', fontWeight: '800', color: card.cor }}>
                            {card.valor}
                        </p>
                        <p style={{ margin: 0, fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: CORES.gray600 }}>
                            {card.label}
                        </p>
                    </div>
                ))}
            </div>

            {/* Lista de erros */}
            {erros?.length > 0 && (
                <div style={{ border: '1px solid #fecaca', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{
                        padding: '10px 16px', backgroundColor: '#fef2f2',
                        borderBottom: '1px solid #fecaca',
                        display: 'flex', alignItems: 'center', gap: '8px',
                    }}>
                        <FontAwesomeIcon icon="exclamation-triangle" style={{ color: CORES.danger }} />
                        <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#991b1b' }}>
                            {erros.length} erro(s) encontrado(s)
                        </span>
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {erros.map((erro, i) => (
                            <div key={i} style={{
                                padding: '10px 16px',
                                borderBottom: i < erros.length - 1 ? '1px solid #fee2e2' : 'none',
                                display: 'flex', gap: '12px', alignItems: 'flex-start',
                            }}>
                                <span style={{
                                    padding: '1px 7px', borderRadius: '4px',
                                    backgroundColor: '#fee2e2', color: CORES.danger,
                                    fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', flexShrink: 0,
                                }}>
                                    Linha {erro.linha}
                                </span>
                                <span style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#7f1d1d' }}>
                                    {erro.motivo}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Ações */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={onNovaImportacao} style={btnSecundarioStyle}>
                    <FontAwesomeIcon icon="redo" /> Nova Importação
                </button>
                <button onClick={onFechar} style={btnPrimaryStyle}>
                    <FontAwesomeIcon icon="check" /> Concluir
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Estilos compartilhados
// ─────────────────────────────────────────────────────────────────────────────

const selectStyle = {
    width: '100%',
    padding: '7px 10px',
    border: `1px solid ${CORES.gray200}`,
    borderRadius: '7px',
    fontSize: `calc(13px * var(--font-scale))`,
    color: CORES.gray700,
    backgroundColor: '#fff',
    outline: 'none',
    cursor: 'pointer',
};

const btnPrimaryStyle = {
    padding: '9px 20px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff', border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600',
    display: 'flex', alignItems: 'center', gap: '7px',
    boxShadow: '0 1px 3px rgba(59,130,246,0.4)',
};

const btnSecundarioStyle = {
    padding: '9px 20px',
    backgroundColor: '#fff',
    color: CORES.gray700,
    border: `1px solid ${CORES.gray200}`,
    borderRadius: '8px',
    cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600',
    display: 'flex', alignItems: 'center', gap: '7px',
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal: LeadImportModal
// ─────────────────────────────────────────────────────────────────────────────

const LeadImportModal = ({ show, onHide, pipelines, colaboradores, onImportSuccess }) => {
    const [etapa,        setEtapa]        = useState(ETAPAS.UPLOAD);
    const [previewData,  setPreviewData]  = useState(null);
    const [mapping,      setMapping]      = useState({});
    const [config,       setConfig]       = useState({
        pipeline_id:    '',
        estagio_id:     '',
        responsavel_id: '',
        duplicatas:     'atualizar',
    });
    const [resultado,    setResultado]    = useState(null);
    const [jobId,        setJobId]        = useState(null);   // ← ID do job em background
    const [isProcessing, setIsProcessing] = useState(false);
    const [erroGlobal,   setErroGlobal]   = useState(null);

    // Inicializa pipeline padrão quando o modal abre
    useEffect(() => {
        if (show && pipelines.length > 0 && !config.pipeline_id) {
            const padrao = pipelines.find(p => p.is_default) || pipelines[0];
            const primeiroEstagio = padrao?.estagios?.[0];
            setConfig(prev => ({
                ...prev,
                pipeline_id: padrao?.id || '',
                estagio_id:  primeiroEstagio?.id || '',
            }));
        }
    }, [show, pipelines]); // eslint-disable-line react-hooks/exhaustive-deps

    const resetar = () => {
        setEtapa(ETAPAS.UPLOAD);
        setPreviewData(null);
        setMapping({});
        setResultado(null);
        setJobId(null);
        setErroGlobal(null);
        setIsProcessing(false);
    };

    const handlePreviewSuccess = (data) => {
        setPreviewData(data);
        setMapping(data.sugestao_mapping || {});
        setErroGlobal(null);
        setEtapa(ETAPAS.MAPEAMENTO);
    };

    // Dispara o job em background e avança para a etapa de resultado/polling
    const handleProcessar = async () => {
        if (!config.pipeline_id || !config.estagio_id) {
            setErroGlobal('Selecione um pipeline e um estágio antes de continuar.');
            return;
        }
        if (!Object.values(mapping).includes('nome')) {
            setErroGlobal('É obrigatório mapear pelo menos o campo "Nome".');
            return;
        }

        setIsProcessing(true);
        setErroGlobal(null);

        try {
            // O backend retorna { job_id, status, total } imediatamente (202)
            const response = await axios.post('/api/leads/import/process', {
                pipeline_id:    parseInt(config.pipeline_id),
                estagio_id:     parseInt(config.estagio_id),
                responsavel_id: config.responsavel_id ? parseInt(config.responsavel_id) : null,
                duplicatas:     config.duplicatas,
                mapping:        mapping,
                data:           previewData.data,
            });

            setJobId(response.data.job_id);
            setResultado(null);          // ainda sem resultado — polling vai preencher
            setEtapa(ETAPAS.RESULTADO);

        } catch (err) {
            setErroGlobal(err.response?.data?.message || 'Erro ao iniciar a importação.');
        } finally {
            setIsProcessing(false);
        }
    };

    // Chamado pelo EtapaAguardando quando o polling detecta status "done"
    const handleJobConcluido = (resultadoFinal) => {
        setResultado(resultadoFinal);

        if (resultadoFinal?.importados > 0 || resultadoFinal?.atualizados > 0) {
            onImportSuccess?.();
        }
    };

    // Chamado pelo EtapaAguardando quando o polling detecta status "failed"
    const handleJobErro = (mensagem) => {
        setEtapa(ETAPAS.MAPEAMENTO);   // volta para o mapeamento
        setErroGlobal(mensagem);
    };

    if (!show) return null;

    const podeAvancar = etapa === ETAPAS.MAPEAMENTO
        && config.pipeline_id
        && config.estagio_id
        && Object.values(mapping).includes('nome');

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onHide}
                style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1050,
                    backdropFilter: 'blur(2px)',
                }}
            />

            {/* Modal */}
            <div style={{
                position: 'fixed',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1051,
                width: '90vw',
                maxWidth: etapa === ETAPAS.MAPEAMENTO ? '860px' : '600px',
                maxHeight: '90vh',
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'max-width 0.3s ease',
            }}>

                {/* Header */}
                <div style={{
                    padding: '20px 24px 0',
                    borderBottom: `1px solid ${CORES.gray100}`,
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '10px',
                                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <FontAwesomeIcon icon="file-import" style={{ color: '#fff', fontSize: '16px' }} />
                            </div>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: CORES.gray900 }}>
                                    Importar Leads
                                </h2>
                                <p style={{ margin: 0, fontSize: `calc(12px * var(--font-scale))`, color: CORES.gray400 }}>
                                    Importe leads de qualquer formato de arquivo
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onHide}
                            style={{
                                width: '32px', height: '32px', borderRadius: '8px',
                                border: 'none', backgroundColor: CORES.gray100,
                                cursor: 'pointer', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                color: CORES.gray600, fontSize: `calc(14px * var(--font-scale))`,
                                flexShrink: 0,
                            }}
                        >
                            <FontAwesomeIcon icon="times" />
                        </button>
                    </div>
                    <Stepper etapaAtual={etapa} />
                </div>

                {/* Corpo */}
                <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>

                    {/* Erro global */}
                    {erroGlobal && (
                        <div style={{
                            marginBottom: '16px', padding: '10px 14px',
                            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                            borderRadius: '8px', display: 'flex', gap: '8px', alignItems: 'center',
                            fontSize: `calc(13px * var(--font-scale))`, color: '#991b1b',
                        }}>
                            <FontAwesomeIcon icon="exclamation-circle" style={{ flexShrink: 0 }} />
                            {erroGlobal}
                        </div>
                    )}

                    {/* Etapa 1 */}
                    {etapa === ETAPAS.UPLOAD && (
                        <EtapaUpload
                            onPreviewSuccess={handlePreviewSuccess}
                            onError={(msg) => setErroGlobal(msg)}
                        />
                    )}

                    {/* Etapa 2 */}
                    {etapa === ETAPAS.MAPEAMENTO && previewData && (
                        <EtapaMapeamento
                            previewData={previewData}
                            pipelines={pipelines}
                            colaboradores={colaboradores}
                            mapping={mapping}
                            setMapping={setMapping}
                            config={config}
                            setConfig={setConfig}
                        />
                    )}

                    {/* Etapa 3 — polling enquanto resultado === null, resultado final quando preenchido */}
                    {etapa === ETAPAS.RESULTADO && (
                        <EtapaResultado
                            resultado={resultado}
                            jobId={jobId}
                            onConcluido={handleJobConcluido}
                            onErro={handleJobErro}
                            onFechar={onHide}
                            onNovaImportacao={resetar}
                        />
                    )}
                </div>

                {/* Footer — oculto na etapa de resultado */}
                {etapa !== ETAPAS.RESULTADO && (
                    <div style={{
                        padding: '16px 24px',
                        borderTop: `1px solid ${CORES.gray100}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        flexShrink: 0, backgroundColor: '#fafafa',
                    }}>
                        <button
                            onClick={etapa === ETAPAS.UPLOAD ? onHide : resetar}
                            style={btnSecundarioStyle}
                        >
                            <FontAwesomeIcon icon={etapa === ETAPAS.UPLOAD ? 'times' : 'arrow-left'} />
                            {etapa === ETAPAS.UPLOAD ? 'Cancelar' : 'Voltar'}
                        </button>

                        {etapa === ETAPAS.MAPEAMENTO && (
                            <button
                                onClick={handleProcessar}
                                disabled={!podeAvancar || isProcessing}
                                style={{
                                    ...btnPrimaryStyle,
                                    opacity: (!podeAvancar || isProcessing) ? 0.6 : 1,
                                    cursor: (!podeAvancar || isProcessing) ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {isProcessing ? (
                                    <>
                                        <div style={{
                                            width: '14px', height: '14px', borderRadius: '50%',
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            borderTopColor: '#fff',
                                            animation: 'spin 0.8s linear infinite',
                                        }} />
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon="file-import" />
                                        Importar {previewData?.total_registros} leads
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
    );
};

export default LeadImportModal;
